import { useState, useEffect } from "react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { MdAttachMoney } from "react-icons/md";
import { useVmCostByMonth } from "../../../hooks/useVmCostsByMonth";
import HistoryDateFilter from "../../common/HistoryDateFilter";


const COLORS = ["#00C2FF", "#FFB800", "#FF6B6B", "#51CF66", "#FF8787", "#845EF7", "#FCC419", "#20C997"];

const formatDateForApi = (date) => {
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

const parseMonthDate = (dateStr) => {
  const [day, month, year] = dateStr.split('/');
  return new Date(parseInt(year), parseInt(month) - 1, 1);
};

const formatMonthValue = (date) => {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
};

const formatDisplayMonth = (dateStr) => {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  return date.toLocaleString('default', { month: 'short', year: '2-digit' });
};

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    const formatAmount = (amount) => {
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
      }).format(amount || 0);
    };
    
    return (
      <div className="bg-primary-dark border border-gray-700 rounded-lg p-3 shadow-lg">
        <p className="text-white text-sm font-medium mb-2">{label}</p>
        {payload.map((entry, index) => (
          <p key={index} className="text-gray-300 text-xs mb-1">
            <span style={{ color: entry.color }}>●</span> {entry.name}: {formatAmount(entry.value)}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

const VmCostHistoryTable = ({ vmIds, vmNames }) => {
  const { getVmCostHistoryByDateRange, loading } = useVmCostByMonth();
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [appliedStartDate, setAppliedStartDate] = useState("");
  const [appliedEndDate, setAppliedEndDate] = useState("");
  const [chartData, setChartData] = useState([]);
  const [vmNameList, setVmNameList] = useState([]);
  const [fetchLoading, setFetchLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleRangeChange = (start, end) => {
    setStartDate(start);
    setEndDate(end);
    setAppliedStartDate(start);
    setAppliedEndDate(end);
  };

  useEffect(() => {
    if (appliedStartDate && appliedEndDate && vmIds && vmIds.length > 0) {
      fetchCostHistory();
    }
  }, [appliedStartDate, appliedEndDate, vmIds]);

  const fetchCostHistory = async () => {
    setFetchLoading(true);
    setError(null);
    try {
      const allVmData = [];
      const names = [];
      
      for (let i = 0; i < vmIds.length; i++) {
        const vmId = vmIds[i];
        const vmName = vmNames && vmNames[i] ? vmNames[i] : `VM ${vmId}`;
        const result = await getVmCostHistoryByDateRange(vmId, appliedStartDate, appliedEndDate);
        if (result.success && result.data) {
          allVmData.push({
            vmId: result.data.vmId,
            vmName: vmName,
            costs: result.data.costs
          });
          names.push(vmName);
        }
      }
      setVmNameList(names);

      if (allVmData.length === 0) {
        setChartData([]);
        setError("No cost history data available");
        return;
      }

      const monthlyDataMap = new Map();

      allVmData.forEach(vm => {
        vm.costs.forEach(cost => {
          if (cost.totalCost !== null && cost.totalCost !== undefined && cost.totalCost !== 0) {
            if (!monthlyDataMap.has(cost.date)) {
              monthlyDataMap.set(cost.date, { date: cost.date });
            }
            const monthData = monthlyDataMap.get(cost.date);
            monthData[vm.vmName] = cost.totalCost;
          }
        });
      });

      const sortedMonths = Array.from(monthlyDataMap.keys()).sort();
      
      const transformedData = sortedMonths.map(month => {
        const dataPoint = { date: formatDisplayMonth(month) };
        allVmData.forEach(vm => {
          const monthData = monthlyDataMap.get(month);
          if (monthData && monthData[vm.vmName] !== undefined) {
            dataPoint[vm.vmName] = monthData[vm.vmName];
          } else {
            dataPoint[vm.vmName] = null;
          }
        });
        return dataPoint;
      });

      setChartData(transformedData);
    } catch (err) {
      console.error("Error fetching cost history:", err);
      setError("Failed to load cost history data");
    } finally {
      setFetchLoading(false);
    }
  };

  if (!vmIds || vmIds.length === 0) {
    return (
      <div className="bg-primary-dark rounded-2xl p-6 m-4">
        <div className="text-center py-8">
          <p className="text-gray-400">No VM selected</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-primary-dark rounded-2xl p-6 m-2">
      <div className="flex items-center justify-between gap-2 mb-6 pb-3 border-b border-gray-700">
        <div className="flex items-center gap-2">
          <MdAttachMoney className="text-primary-cyan text-2xl" />
          <h3 className="text-xl font-semibold text-white">VM Cost History</h3>
        </div>
        <HistoryDateFilter onRangeChange={handleRangeChange} />
      </div>

      <div className="mt-6">
        {(fetchLoading || loading) ? (
          <div className="text-center py-8">
            <div className="inline-block w-8 h-8 border-4 border-primary-cyan border-t-transparent rounded-full animate-spin"></div>
            <p className="text-gray-400 mt-3">Loading cost history...</p>
          </div>
        ) : error ? (
          <div className="text-center py-8">
            <p className="text-red-400">{error}</p>
          </div>
        ) : chartData.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-gray-400">No cost history available for selected period</p>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={400}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
              <XAxis dataKey="date" stroke="#9CA3AF" />
              <YAxis 
                stroke="#9CA3AF" 
                tickFormatter={(value) => `$${value}`}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend wrapperStyle={{ color: "#9CA3AF" }} />
              {vmNameList.map((vmName, index) => (
                <Line
                  key={vmName}
                  type="monotone"
                  dataKey={vmName}
                  stroke={COLORS[index % COLORS.length]}
                  strokeWidth={2}
                  dot={{ r: 4, fill: COLORS[index % COLORS.length] }}
                  activeDot={{ r: 6 }}
                  connectNulls
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
};

export default VmCostHistoryTable;